import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { X } from "lucide-react";
import SearchBrokerageAgentTeam, {
  type SearchBrokerageAgentTeamHandle,
} from "components/SearchBrokerageAgentTeam";
import "components/SearchBrokerageAgentTeam.css";

export type SendOrderEmailRecipient = {
  id: string;
  email: string;
  name: string;
  licenseType?: string;
};

export type SendOrderEmailRecipientsModalProps = {
  show: boolean;
  /** Modal title, e.g. "Send Quote" / "Send Invoice" */
  title?: string;
  token: string;
  /** Optional; when omitted the modal loads the first customer group. */
  customerGroupId?: string | null;
  /** Shown so users know the order end-user always receives the email. */
  primaryEmailHint?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirming?: boolean;
  onCancel: () => void;
  /**
   * Called only when the user clicks OK.
   * `additionalEmails` may be empty (end-user only).
   */
  onConfirm: (additionalEmails: string[], recipients?: SendOrderEmailRecipient[]) => void | Promise<void>;
  /**
   * Batch invoice: send only emails selected in this modal.
   * Disables OK until at least one recipient is picked.
   */
  selectedRecipientsOnly?: boolean;
};

function firstEmail(raw: unknown): string {
  if (raw == null) return "";
  return String(raw).trim().split("||")[0].trim();
}

function recipientKey(email: string): string {
  return email.trim().toLowerCase();
}

const MODAL_STYLE = `
  .so-email-recipients-modal .modal-dialog {
    max-width: 520px;
    width: calc(100% - 1.5rem);
  }
  .so-email-recipients-modal .modal-content {
    border-radius: 12px;
    border: 1px solid #e7eaee;
    left: 62px !important;
    top: -18px !important;
  }
  .so-email-recipients-modal .modal-header {
    border-bottom: 1px solid #e7eaee;
    padding: 1rem 1.25rem;
  }
  .so-email-recipients-modal .modal-body {
    padding: 1rem 1.25rem;
  }
  .so-email-recipients-modal .modal-footer {
    border-top: 1px solid #e7eaee;
    padding: 0.85rem 1.25rem;
  }
  .so-email-recipients-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 0.75rem;
    background-color: #e8f0ff;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
  }
  .so-email-recipients-chip-label {
    color: #155dfc;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .so-email-recipients-chip-remove {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #155dfc;
    border: none;
    background: none;
    border-radius: 50%;
    padding: 0;
    line-height: 1;
  }
  .so-email-recipients-chip-remove:hover:not(:disabled) {
    background-color: rgba(21, 93, 252, 0.12);
  }
  .so-email-recipients-chip-remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Reusable Send Quote / Send Invoice recipient picker.
 * End-user email is always sent by the parent; this modal only collects extra CC recipients.
 * Used by sales, installation, removal (and future batch payment flows).
 */
export default function SendOrderEmailRecipientsModal({
  show,
  title = "Send email",
  token,
  customerGroupId: customerGroupIdProp,
  primaryEmailHint,
  selectedRecipientsOnly = false,
  confirmLabel = "OK",
  cancelLabel = "Cancel",
  confirming = false,
  onCancel,
  onConfirm,
}: SendOrderEmailRecipientsModalProps) {
  const searchRef = useRef<SearchBrokerageAgentTeamHandle | null>(null);
  const [customerGroupId, setCustomerGroupId] = useState<string>(
    String(customerGroupIdProp || "").trim()
  );
  const [selected, setSelected] = useState<SendOrderEmailRecipient[]>([]);
  const [pickHint, setPickHint] = useState("");
  const [confirmingLocal, setConfirmingLocal] = useState(false);

  const busy = confirming || confirmingLocal;

  useEffect(() => {
    if (!show) return;
    setSelected([]);
    setPickHint("");
    setConfirmingLocal(false);
    searchRef.current?.reset?.();
  }, [show]);

  useEffect(() => {
    const fromProp = String(customerGroupIdProp || "").trim();
    if (fromProp) {
      setCustomerGroupId(fromProp);
      return;
    }
    if (!show || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) return;
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        const firstId = list[0]?._id || list[0]?.id || "";
        if (!cancelled && firstId) setCustomerGroupId(String(firstId));
      } catch {
        /* ignore — search will simply have no group */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, token, customerGroupIdProp]);

  const addRecipient = useCallback((payload: {
    email?: string;
    name?: string;
    id?: string;
    licenseType?: string;
  }) => {
    const email = firstEmail(payload.email);
    if (!email || !email.includes("@")) {
      setPickHint("Selected contact has no email. Pick someone with an email address.");
      return;
    }
    setSelected((prev) => {
      if (prev.some((r) => recipientKey(r.email) === recipientKey(email))) {
        setPickHint("That email is already in the list.");
        return prev;
      }
      setPickHint("");
      return [
        ...prev,
        {
          id: String(payload.id || email),
          email,
          name: String(payload.name || "").trim() || email,
          licenseType: payload.licenseType,
        },
      ];
    });
    // Allow picking another recipient immediately.
    window.setTimeout(() => searchRef.current?.reset?.(), 0);
  }, []);

  const removeRecipient = useCallback((email: string) => {
    setSelected((prev) => prev.filter((r) => recipientKey(r.email) !== recipientKey(email)));
  }, []);

  const handleContinue = useCallback(
    (payload: {
      type: string;
      item: any;
      contact: { fName: string; lName: string; email: string; phone: string };
    }) => {
      if (!payload?.item) return;
      const email = firstEmail(payload.contact?.email || payload.item?.email);
      const name =
        String(payload.item?.displayLabel || payload.item?.label || "").trim() ||
        [payload.contact?.fName, payload.contact?.lName].filter(Boolean).join(" ").trim();
      addRecipient({
        email,
        name,
        id: String(payload.item?.value || payload.item?._id || payload.item?.id || email),
        licenseType: String(
          payload.item?.license_type || payload.item?.licenseType || payload.type || ""
        ).trim(),
      });
    },
    [addRecipient]
  );

  const handleOk = async () => {
    if (busy) return;
    if (selectedRecipientsOnly && selected.length === 0) {
      setPickHint("Select at least one recipient email.");
      return;
    }
    setConfirmingLocal(true);
    try {
      await onConfirm(selected.map((r) => r.email), selected);
    } finally {
      setConfirmingLocal(false);
    }
  };

  return (
    <>
      <style>{MODAL_STYLE}</style>
      <Modal
        show={show}
        onHide={() => {
          if (!busy) onCancel();
        }}
        centered
        dialogClassName="so-email-recipients-modal"
        backdrop="static"
        keyboard={!busy}
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title as="h5" className="mb-0 fw-bold">
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-dark mb-3" style={{ fontSize: 13 }}>
          {selectedRecipientsOnly
            ? "Search and select the people who should receive this invoice, then click OK. Only selected emails will be sent."
            : "The primary recipient will always receive this email. You can optionally search for and select additional recipients to CC below."}

            {/* The order end user always receives this email
            {primaryEmailHint ? (
              <>
                {" "}
                (<strong>{primaryEmailHint}</strong>)
              </>
            ) : null}
            . Optionally search and select additional recipients below, then click {confirmLabel}. */}
          </p>

          <div className="so-email-recipients-search mb-2">
            <SearchBrokerageAgentTeam
              ref={searchRef}
              key={show ? "open" : "closed"}
              customerGroupId={customerGroupId}
              token={token}
              emailPickerMode
              title={title}
              onContinue={handleContinue}
            />
          </div>

          {pickHint ? (
            <div className="text-danger mb-2" style={{ fontSize: 12 }}>
              {pickHint}
            </div>
          ) : null}

          {selected.length > 0 ? (
            <div className="mb-1">
              <label className="form-label fw-semibold mb-2" style={{ fontSize: 13 }}>
                Selected emails
              </label>
              <div className="d-flex flex-wrap gap-2">
                {selected.map((r) => (
                  <div key={recipientKey(r.email)} className="so-email-recipients-chip">
                    <span className="so-email-recipients-chip-label" title={r.email}>
                      {r.email}
                    </span>
                    <button
                      type="button"
                      className="so-email-recipients-chip-remove"
                      aria-label={`Remove ${r.email}`}
                      disabled={busy}
                      onClick={() => removeRecipient(r.email)}
                      title={`Remove ${r.email}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div  style={{ fontSize: 12 }}>
              {selectedRecipientsOnly
                ? "No recipients selected. Select at least one email to send."
                : "No additional recipients selected. The email will still be sent to the primary recipient."}

              {/* No additional emails selected — {confirmLabel} will still email the end user. */}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="btn back-btn surface-btn fw-bold fs-16"
            disabled={busy}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn-primary save-btn surface-btn fw-bold fs-16 px-4"
            disabled={busy || (selectedRecipientsOnly && selected.length === 0)}
            onClick={() => void handleOk()}
          >
            {busy ? "Sending…" : confirmLabel}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

/** Merge extra recipient emails into an existing parallel emails/licenseTypes list (deduped). */
export function appendAdditionalRecipientEmails(
  emails: string[],
  licenseTypes: string[],
  additionalEmails: string[],
  licenseTypeForAdditional = "AGENT"
): { emails: string[]; licenseTypes: string[] } {
  const nextEmails = [...emails];
  const nextTypes = [...licenseTypes];
  const seen = new Set(nextEmails.map((e) => recipientKey(e)));
  for (const raw of additionalEmails || []) {
    const email = firstEmail(raw);
    if (!email || !email.includes("@")) continue;
    const key = recipientKey(email);
    if (seen.has(key)) continue;
    seen.add(key);
    nextEmails.push(email);
    nextTypes.push(licenseTypeForAdditional);
  }
  return { emails: nextEmails, licenseTypes: nextTypes };
}

/** Merge extra emails into an invoice customer recipient bundle (deduped). */
export function mergeAdditionalIntoInvoiceCustomerRecipients(
  customerRecipients: { emails: string[]; licenseTypes: string[] },
  additionalEmails: string[]
): { emails: string[]; licenseTypes: string[] } {
  return appendAdditionalRecipientEmails(
    customerRecipients.emails || [],
    customerRecipients.licenseTypes || [],
    additionalEmails
  );
}
